<script setup>
import { computed, useAttrs } from 'vue';

import { useTheme } from '../composables/useTheme';

/**
 * Collapsible section with a labeled header that toggles the content area open and closed.
 *
 * @status recommended
 * @group navigation
 * @useWhen Showing secondary content under a labeled header that users can expand on demand
 * @see https://quasar.dev/vue-components/expansion-item Quasar q-expansion-item API
 */
defineOptions({ name: 'HrExpansionItem' });

const props = defineProps({
  /** Header label text. */
  label: {
    type: String,
    default: null,
  },
  /** Supporting text shown below the header label. */
  caption: {
    type: String,
    default: null,
  },
  /** Controlled open state; null means the component manages state internally. */
  modelValue: {
    type: Boolean,
    default: null,
  },
  /** Initial expanded state when uncontrolled (modelValue is null). */
  defaultOpened: {
    type: Boolean,
    default: false,
  },
  /** Icon name displayed in the header. */
  icon: {
    type: String,
    default: null,
  },
  /** Reduces header height to the dense density level. */
  dense: {
    type: Boolean,
    default: false,
  },
  /** Prevents toggling and dims the header. */
  disable: {
    type: Boolean,
    default: false,
  },
  /**
   * Renders the row as an M3 "segmented" list row — a filled, rounded, inset card, matching
   * HrItem's treatment so an expanding row and a plain one read as the same kind of card.
   */
  segmented: {
    type: Boolean,
    default: false,
  },
  /**
   * Removes the segmented card's horizontal inset so it sits flush with its container.
   *
   * `segmented` insets each card by 8px, which is right for a list that owns its surface. It is
   * wrong wherever the list sits inside another layout — a form grid, say — where every neighboring
   * control is flush to the column and the inset reads as misalignment. Has no effect without
   * `segmented`.
   */
  flush: {
    type: Boolean,
    default: false,
  },
  /**
   * Legacy props are any additional Quasar props that can be passed for backwards
   * compatibility with the legacy theme. These props will be ignored when a design system theme
   * is active.
   * @type {Object} legacyProps
   * @deprecated This prop is only for backwards compatibility with the legacy theme and should be
   * removed once the legacy theme is removed.
   */
  legacyProps: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits({
  /** Emitted when the open state changes; payload is the new boolean value. */
  'update:modelValue': null,
});

const attrs = useAttrs();
const { hasTheme } = useTheme();

/*
 * `segmented` is load-bearing in every theme, so it outlives the gate the rest of the M3 treatment
 * sits behind. It is not a color opinion legacy can decline: it says these rows are discrete cards,
 * and a list whose rows lose their edges in one theme reads as a different component there.
 */
const classes = computed(() => [
  hasTheme.value && 'hr-expansion-item',
  props.segmented && 'hr-expansion-item--segmented',
  props.flush && 'hr-expansion-item--flush',
]);

const effectiveProps = computed(() => {
  const base = {
    ...(props.modelValue !== null && { modelValue: props.modelValue }),
    ...(props.label !== null && { label: props.label }),
    ...(props.caption !== null && { caption: props.caption }),
    ...(props.icon !== null && { icon: props.icon }),
    defaultOpened: props.defaultOpened,
    dense: props.dense,
    disable: props.disable,
  };

  if (hasTheme.value) {
    return { ...base, ...attrs };
  }
  return { ...base, ...props.legacyProps, ...attrs };
});
</script>

<template>
  <q-expansion-item
    v-bind="effectiveProps"
    :class="classes"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <template
      v-if="$slots.header"
      #header
    >
      <!-- @slot Replaces the header row, keeping the toggle icon. For a trailing action. -->
      <slot name="header" />
    </template>
    <!-- @slot Expandable content shown when the item is open. -->
    <slot />
  </q-expansion-item>
</template>

<style lang="scss" scoped>

/* Only applies to light/dark themes */

/* ── Header row ──────────────────────────────────────────────────────────── */
/*
 * Set on-surface so currentColor cascades correctly to Quasar's state-layer
 * helpers (.q-focus-helper uses currentColor for the hover/focus overlay).
 */
.hr-expansion-item :deep(.q-item) {
  color: var(--app-on-surface);
}

/* ── Label color ─────────────────────────────────────────────────────────── */
.hr-expansion-item :deep(.q-item__label) {
  color: var(--app-on-surface);
}

/* ── Segmented ───────────────────────────────────────────────────────────── */
/*
 * The same filled card HrItem renders for `segmented`, applied to the whole expansion rather than
 * to its header, so the revealed content sits inside the card instead of spilling below it.
 * `overflow: hidden` is what keeps that content within the rounded corners.
 */
.hr-expansion-item--segmented {
  margin: 2px 8px;
  border-radius: var(--app-shape-corner-medium);
  overflow: hidden;
  background: var(--app-surface-container-high);
}

/*
 * Flush variant: the card keeps its fill, radius and vertical rhythm and gives up only the
 * horizontal inset. `margin-inline` rather than left/right, because RTL is enabled.
 */
.hr-expansion-item--segmented.hr-expansion-item--flush {
  margin-inline: 0;
}

/* ── Expand icon ─────────────────────────────────────────────────────────── */
.hr-expansion-item :deep(.q-expansion-item__toggle-icon) {
  color: var(--app-on-surface-variant);
}

/* ── State layer ─────────────────────────────────────────────────────────── */
/*
 * Reduce Quasar's default 12% hover opacity to M3's 8%.
 * body.desktop mirrors Quasar's own selector to win on specificity.
 */
/* stylelint-disable-next-line selector-max-type */
body.desktop .hr-expansion-item :deep(.q-item--clickable:hover .q-focus-helper) {
  opacity: 0.08;
}

/* ── Dense (direct prop) ─────────────────────────────────────────────────── */
/*
 * When dense is set directly on the component, Quasar adds q-item--dense to
 * the internal header q-item. Use label-large (14sp) to match M3 menu item typography.
 */
.hr-expansion-item :deep(.q-item--dense .q-item__label) {
  font-size:      var(--app-type-label-large-size);
  line-height:    var(--app-type-label-large-line-height);
  font-weight:    var(--app-type-label-large-weight);
  letter-spacing: var(--app-type-label-large-tracking);
}

/* ── Disabled ────────────────────────────────────────────────────────────── */
.hr-expansion-item :deep(.q-item--disabled .q-item__label) {
  color: color-mix(in srgb, var(--app-on-surface) 38%, transparent);
}

.hr-expansion-item :deep(.q-item--disabled .q-expansion-item__toggle-icon) {
  color: color-mix(in srgb, var(--app-on-surface-variant) 38%, transparent);
}

</style>

<style lang="scss">

/*
 * Not scoped — Vue 3's provide/inject for Quasar's dense context only reaches
 * direct children of q-list, not slot content passed through HrList. CSS
 * cascade reaches the DOM descendants regardless. [data-theme] ensures these
 * rules only apply when a design system theme is active.
 */
[data-theme] .hr-list--dense .hr-expansion-item .q-item__label {
  font-size:      var(--app-type-label-large-size);
  line-height:    var(--app-type-label-large-line-height);
  font-weight:    var(--app-type-label-large-weight);
  letter-spacing: var(--app-type-label-large-tracking);
}

</style>
